/*
* Copyright © 2019 Insticator, Inc. All Rights Reserved.
*
* CONFIDENTIALITY NOTICE: THIS SOFTWARE CONTAINS CONFIDENTIAL INFORMATION OF
* INSTICATOR, INC. USE, DISCLOSURE OR REPRODUCTION IS PROHIBITED WITHOUT THE PRIOR
* EXPRESS WRITTEN PERMISSION OF INSTICATOR, INC.
*/


// IN VIEW FUNCTIONALITY IS NOT CURRENTLY AVAILABLE FOR DFP SAFE FRAME INTEGRATION.
// We are currently using scroll event listeners to determent whether or not the embed is visible.
// However this method is expensive and couldn't be used for the embedded through a safe iframe embeds.
// A potential solution is to use an Intersection Observer which is a new API  that can be used to understand
// the visibility and position of DOM elements ("targets") relative to a containing element or to the top-level viewport ("root").
// https://developer.mozilla.org/en-US/docs/Web/API/Intersection_Observer_API
// https://www.w3.org/TR/intersection-observer/



/* ++++++++++ --------------- IMPORTS --------------- ++++++++++ */
import { log } from 'utils/logger';
// utils
import { integration, environment } from 'utils/environment';
import { trackEvent } from 'utils/tracking/tracking';
import Timer from 'utils/timer';


/* ++++++++++ --------------- EXPORTS --------------- ++++++++++ */
export default class InView {
    constructor(inViewSetting, setInViewStatus, isInView) {
        this.inViewSetting = inViewSetting;             // 'none', 'firstInThenOutOfView', 'firstInView'
        this.setInViewStatus = setInViewStatus;         // Callback method which is passed from the App Component. This is an action that sets inView property of the embedStatus slice of state.
        this.isInView = isInView;                       // null, true, false.
        this.embedIntegration = integration;            // `DFP_WITHOUT_SAFEFRAME`, `HARD_CODED_WITH_FRIENDLYFRAME`, `DFP_WITH_SAFEFRAME`, `HARD_CODED_WITH_SAFEFRAME`, `HARD_CODING`
        this.mostParentIframe = this.getMostParentIframe();
        this.isScrollListenerSet = false;
        this.pingIntervalId = null;
        this.pingIntervalTime = 1000;
        this.timer = new Timer();                       // used for tracking time spent
        this.checkInView = this.checkInView.bind(this);
    }
    initializeInViewCheck() {
        if(this.isAvailable()) {
            // Check if the embed is already in view.
            this.checkInView();
            // Set up a scroll listener to check if the embed is in view.
            // Even is the embed is already in view we need to know when it gets out of the view.
            this.setupScrollListener();
            // In some cases an embed loads above the viewport and then pushed in view by loading content.
            // In cases like the initial check would set the in-view state to false.
            // To get the updated status and remove a Splash Screen make a continuous inview check for the first 10 seconds.
            this.initializeInViewPing();
        } else {
            this.sendTrackingEvent('unavailable')
        }
    }
    isAvailable() {
        // InView check is not available for any safe iframe integrations since it needs to access the most top window.
        // InView check can also be disabled from the settings.
        return !(this.inViewSetting === 'none' || this.embedIntegration === 'HARD_CODED_WITH_SAFEFRAME' || this.embedIntegration === 'DFP_WITH_SAFEFRAME');
    }
    setupScrollListener() {
        if (!!this.isInView) { return null; }
        log('IN_VIEW', 'setupScrollListener');
        switch (this.embedIntegration) {
            case 'DFP_WITHOUT_SAFEFRAME':
            // fall through
            case 'HARD_CODED_WITH_FRIENDLYFRAME':
                window.parent.parent.addEventListener('scroll', this.checkInView, false);
                break;
            default:
                window.parent.addEventListener('scroll', this.checkInView, false);
        }
        this.isScrollListenerSet = true;
    }
    initializeInViewPing() {
        let pingCount = 0;
        if (!this.isInView) this.pingIntervalId = setInterval(() => {
            log('IN_VIEW', 'PING_CHECK');
            if(!this.isInView) this.checkInView();
            if (pingCount === 10000 / this.pingIntervalTime || this.isInView) this.stopInViewPing();
            pingCount += 1;
        }, this.pingIntervalTime)
    }
    stopInViewPing() {
        log('IN_VIEW', 'Clear PING');
        clearInterval(this.pingIntervalId)
    }
    setInViewLocally() {
        this.setInViewStatus(true);
    }
    getMostParentIframe() {
        switch (this.embedIntegration) {
            case `DFP_WITHOUT_SAFEFRAME`:
                // fall through
            case `HARD_CODED_WITH_FRIENDLYFRAME`:
                return window.parent.frameElement;
            default:
                return window.frameElement
        }
    }
    checkInView() {
        log('IN_VIEW', 'checkInView', 'isInViewport:', this.isInViewport());
        this.isInViewport() ? this.handleInView() : this.handleOutOfView();
    }
    handleInView() {
        log('IN_VIEW', 'handleInView');
        log('🎉😺🎉😺🎉 embed is visible 🎉😺🎉😺🎉');
        if (!!this.isInView) { return null; }
        log('IN_VIEW', 'handleInView >>> processing');
        // remove the code below and uncomment the switch statement once BE updates settings for all clients
        this.removeScrollListener();
        this.processInViewStatus(true);
        this.sendTrackingEvent(true);
        this.stopInViewPing();
        // switch (this.inViewSetting) {
        //     case 'firstInView':
        //         this.removeScrollListener();
        //         // fall through
        //     default:
        //         this.processInViewStatus(true);
        //         this.sendTrackingEvent(true);
        //         this.stopInViewPing()
        // }
    }
    handleOutOfView() {
        log('IN_VIEW', 'handleOutOfView', 'inViewStatus:', this.isInView);
        log('💩😾💩😾💩 embed is not visible 💩😾💩😾💩');
        if (this.isInView === null) {
            this.processInViewStatus(false);
        } else if (this.isInView === true) {
            switch (this.inViewSetting) {
                case 'firstInThenOutOfView':
                    // comment back in once BE updates settings
                    // this.removeScrollListener();
                // fall through
                default:
                    this.processInViewStatus(false);
                    this.sendTrackingEvent(false);
            }
        }
    }
    processInViewStatus(isInView) {
        this.setInViewStatus(isInView);
        this.isInView = isInView;
    }
    sendTrackingEvent(inViewStatus) {
        const invewCheckAvailable = inViewStatus !== 'unavailable';
        const isInview = invewCheckAvailable ? inViewStatus : false;
        const timeInView = !!isInview ? {} : { amountOfTimeInView: this.timer.end() };
        if (isInview === true) this.timer.start();
        trackEvent({ type: 'load', name: 'ceuLoad', target: 'embedInView', additionalProps: { embed: { isInview, invewCheckAvailable, ...timeInView } }});
    }
    isInViewport() {
        if (!this.mostParentIframe) return true
        const wrappingIframePosition = this.mostParentIframe.getBoundingClientRect();
        switch(this.embedIntegration) {
            case 'DFP_WITHOUT_SAFEFRAME':
                // fall through
            case 'HARD_CODED_WITH_FRIENDLYFRAME':
                // Top embed position inside dfp iframe. If embedTopPosition is 0 insticator-embed is the most top element inside the dfp iframe.
                // This is the case when the ads are rendered below or on sides of the embed.
                // If the ad is rendered on top of the embed, embedTopPosition will be equal to the height of the ad
                const embedTopPosition = window.frameElement.getBoundingClientRect().top;
                // Bottom embed position
                const embedBottomPosition = window.frameElement.getBoundingClientRect().bottom;
                // Please see the reference for calculating the height https://stackoverflow.com/questions/1248081/get-the-browser-viewport-dimensions-with-javascript
                const dfpIframeHeight = Math.max(window.parent.document.documentElement.clientHeight, window.parent.innerHeight || 0);
                // Browser viewport dimensions
                const viewportHeight = Math.max(window.parent.parent.document.documentElement.clientHeight, window.parent.parent.innerHeight || 0);
                const viewportWidth = Math.max(window.parent.parent.document.documentElement.clientWidth, window.parent.parent.innerWidth|| 0);
                // Checking the parent (dfp) iframe position.
                return wrappingIframePosition.top >= (0 - embedTopPosition) &&
                    wrappingIframePosition.left >= 0 &&
                    (wrappingIframePosition.bottom - (dfpIframeHeight - embedBottomPosition)) <= viewportHeight &&
                    (wrappingIframePosition.bottom - (dfpIframeHeight - embedBottomPosition)) >= 0 &&
                    wrappingIframePosition.right <= viewportWidth;
            case 'HARD_CODING':
            // fall through
            case 'CLOUDFLARE_APP':
            // fall through
            case 'WORDPRESS_PLUGIN':
                return wrappingIframePosition.top >= 0 &&
                    wrappingIframePosition.left >= 0 &&
                    wrappingIframePosition.bottom <= (window.parent.innerHeight || window.parent.document.documentElement.clientHeight) &&
                    wrappingIframePosition.right <= (window.parent.innerWidth || window.parent.document.documentElement.clientWidth);
            default:
                return null;
        }
    }
    removeScrollListener() {
        if(this.isScrollListenerSet) {
            log('IN_VIEW', 'XXXXXXXX REMOVING SCROLL LISTENER XXXXXXXX');
            let element = window.parent.frameElement !== null ? window.parent.parent : window.parent;
            element.removeEventListener('scroll', this.checkInView, false);
            this.isScrollListenerSet = false;
        }
    }
}
